import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, IconButton } from "@mui/material";
import type { ReactNode } from "react";
import { categoryIconGroups, normalizeCategoryIconId } from "../lib/categoryIcons";
import { SidePanel } from "./SidePanel";

type CategoryIconPickerProps = {
  id: string;
  iconDataAttribute?: "account" | "category";
  label: string;
  open: boolean;
  selectedIcon: string;
  onClose: () => void;
  onSelect: (icon: string) => void;
};

export function CategoryIconPicker({ id, iconDataAttribute = "category", label, open, selectedIcon, onClose, onSelect }: CategoryIconPickerProps) {
  const normalizedIcon = normalizeCategoryIconId(selectedIcon);
  const titleId = `${id}Title`;

  return (
    <SidePanel
      ariaLabelledBy={titleId}
      className="category-icon-side-panel"
      contentClassName="category-icon-side-panel-content"
      dataTestId={id}
      open={open}
      onClose={onClose}
    >
      <header className="category-icon-side-panel-toolbar">
        <IconButton className="records-icon-button" type="button" aria-label="Back to editing" title="Back" onClick={onClose}>
          <ArrowBackIcon aria-hidden="true" />
        </IconButton>
        <div>
          <h2 id={titleId}>{label}</h2>
          <p>Choose an icon</p>
        </div>
      </header>
      <Box className="category-icon-picker-scroll">
        <Box className="category-icon-picker" id={id} role="radiogroup" aria-label={label}>
          {categoryIconGroups.map((group) => (
            <Box className="category-icon-group" key={group.title}>
              <h3 className="category-icon-group-title">{group.title}</h3>
              <Box className="category-icon-grid">
                {group.icons.map((option) => (
                  <IconChoice
                    key={option.id}
                    active={option.id === normalizedIcon}
                    dataAttribute={iconDataAttribute}
                    iconId={option.id}
                    label={option.label}
                    onSelect={() => {
                      onSelect(option.id);
                      onClose();
                    }}
                  >
                    <option.Icon aria-hidden="true" />
                  </IconChoice>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </SidePanel>
  );
}

function IconChoice({
  active,
  children,
  dataAttribute,
  iconId,
  label,
  onSelect,
}: {
  active: boolean;
  children: ReactNode;
  dataAttribute: "account" | "category";
  iconId: string;
  label: string;
  onSelect: () => void;
}) {
  const dataProps = dataAttribute === "account" ? { "data-account-icon": iconId } : { "data-category-icon": iconId };

  return (
    <button
      className={`category-icon-choice${active ? " active" : ""}`}
      type="button"
      role="radio"
      aria-label={`${label} icon`}
      aria-checked={active}
      title={label}
      onClick={onSelect}
      {...dataProps}
    >
      {children}
    </button>
  );
}
